/**
 * PoListFormDialog — bám Access spec `frmFactoryQryPoAdd`
 * (`plan/access_all/form_frmFactoryQryPoAdd.txt`).
 *
 * Layout (Access Left/Top coords):
 *   Left col (input Left=2196):
 *     po_no               (Top=180,  required, AfterUpdate duplicate-check)
 *     po_date             (Top=564,  required, date)
 *     po_clossing_date    (Top=948,  date)
 *     quality_code        (Top=1332, ComboBox list_and_valo code/description)
 *     planned_quantity    (Top=1716, number Standard)
 *     in_report_quantity  (Top=2100, number Standard)
 *     out_report_quantity (Top=2484, number Standard)
 *   Right col (input Left=10056):
 *     (Top=180 empty)
 *     is_finished         (Top=564,  ComboBox value-list "0;No;1;Yes")
 *     processing_type     (Top=948,  ComboBox processing_type.po_type)
 *     bom_code            (Top=1332, ComboBox public_v_factory_bom)
 *     note                (Top=1716, multiline Height=1080 — spans rows 5-7)
 *
 * Required (Form_Load line 2272): `strCheck = ",po_no,po_date,"`.
 * Insert fields (strI_mainsub): po_no,po_date,po_clossing_date,quality_code,
 *   planned_quantity,in_report_quantity,out_report_quantity,is_finished,
 *   processing_type,note,bom_code.
 * Update fields (strU_mainsub): same minus po_no (PK — disabled in edit mode).
 * po_no_AfterUpdate (line 2276): duplicate po_no on a new record blocks Save.
 *
 * Hidden / audit fields KHÔNG render: id, id2, is_status, created_*, updated_*.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  useGetReferenceCombosQuery,
  useLazyCheckPoNoExistsQuery,
  useLazyGetSuggestPoNoQuery,
  type FactoryBRowPayload,
  type FactoryCell,
} from '@/store/api/factoryApi'
import {
  useGetBomCropOptionsQuery,
  useGetFactoryBomListQuery,
} from '@/store/api/factoryBomApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type PoRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  po_no: string
  po_date: string
  po_clossing_date: string
  quality_code: string
  planned_quantity: string
  in_report_quantity: string
  out_report_quantity: string
  is_finished: string // '0' | '1'
  processing_type: string
  bom_code: string
  bom_crop: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  po_no: '',
  po_date: '',
  po_clossing_date: '',
  quality_code: '',
  planned_quantity: '',
  in_report_quantity: '',
  out_report_quantity: '',
  is_finished: '0',
  processing_type: '',
  bom_code: '',
  bom_crop: '',
  note: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Bg cho field `Enabled = NotDefault` Access — ở đây là po_no trong edit mode
// (PK, không cho sửa sau khi tạo).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
}

// Access `Format = "Standard"` = `#,##0.00` (thousand sep + 2 decimals).
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

// Parse user input ("1,234.5" / "1234.5" / "") → number | null.
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Format raw value → Standard string (`1,234.56` / `''`).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Format loss như page /data/bom (kind 'percent'): fraction → "0.20%". */
function formatPercent(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return `${(n * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

/** Đảm bảo giá trị hiện tại (đã lưu khi edit) nằm trong options dù master list
 *  không còn chứa nó → tránh Autocomplete hiện blank. */
function injectCurrent(options: string[], current: string): string[] {
  const v = String(current ?? '').trim()
  if (!v || options.includes(v)) return options
  return [v, ...options]
}

/** Combo flat `"<CODE> — <NAME>"` (skill convert-form §ComboBox). Value lưu vẫn
 *  là code; chỉ label hiển thị gắn name. Collapse về "<CODE>" khi không có name
 *  hoặc name trùng code. */
function comboLabel(code: string, nameMap: Record<string, string>): string {
  const c = String(code ?? '').trim()
  if (!c) return ''
  const name = (nameMap[c] ?? '').trim()
  return !name || name === c ? c : `${c} — ${name}`
}

/** Access combo bound column "0;No;1;Yes" → draft '0'/'1'. View có thể trả về
 *  0/1, "No"/"Yes" hoặc boolean — normalize hết về '0'/'1'. */
function normalizeIsFinished(v: FactoryCell): string {
  if (v == null) return '0'
  const s = String(v).trim().toLowerCase()
  if (s === '1' || s === 'yes' || s === 'true') return '1'
  return '0'
}

interface PoListFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  /** Row hiện tại (khi edit). null khi new. */
  row: PoRow | null
  saving: boolean
  factoryCode: string
  /** Toàn bộ rows hiện có — combo options + duplicate po_no check. */
  existingRows: PoRow[]
  /** Permission gating — false → ẩn Save + show read-only banner. */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryBRowPayload) => void
}

export function PoListFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  existingRows,
  canEdit = true,
  onClose,
  onSave,
}: PoListFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  // ── Suggest-No flow (skill access-suggest) ──────────────────────────────
  // po_no auto-suggest khi mở New + regen khi trùng (onBlur). PK po_no
  // serial=False → user thấy & xác nhận, KHÔNG auto-save.
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false) // sync mirror — race guard blur↔Save click
  // bom_crop auto-suggest theo po_date: nhớ giá trị suggest lần trước để chỉ đè
  // khi field còn = suggest (không clobber lựa chọn tay của user).
  const lastAutoCrop = useRef('')
  const [triggerCheck] = useLazyCheckPoNoExistsQuery()
  const [triggerSuggest, suggestState] = useLazyGetSuggestPoNoQuery()

  // Hydrate draft khi dialog mở. New → empty (po_date = today). Edit → row values.
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    lastAutoCrop.current = '' // reset: edit giữ crop đã lưu, không auto-đè lúc mở
    if (mode === 'edit' && row) {
      setDraft({
        po_no: String(row.po_no ?? ''),
        po_date: String(row.po_date ?? '').slice(0, 10),
        po_clossing_date: String(row.po_clossing_date ?? '').slice(0, 10),
        quality_code: String(row.quality_code ?? ''),
        // Pre-format số với Standard `#,##0.00`; user typing auto-format lại trên blur.
        planned_quantity: formatStandard(row.planned_quantity as number | string | null),
        in_report_quantity: formatStandard(row.in_report_quantity as number | string | null),
        out_report_quantity: formatStandard(row.out_report_quantity as number | string | null),
        is_finished: normalizeIsFinished(row.is_finished),
        processing_type: String(row.processing_type ?? ''),
        bom_code: String(row.bom_code ?? ''),
        bom_crop: String(row.bom_crop ?? ''),
        note: String(row.note ?? ''),
      })
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        po_date: new Date().toISOString().slice(0, 10),
      })
    }
  }, [open, mode, row])

  // Auto-suggest po_no khi mở New (KHÔNG dùng useSuggestQuery subscription —
  // chỉ lazy, tránh cache update → re-hydrate, xem skill anti #4). Chỉ set khi
  // field còn trống để không đè giá trị user vừa nhập.
  useEffect(() => {
    if (!open || mode === 'edit') return
    let cancelled = false
    void triggerSuggest({ factoryCode }, false)
      .unwrap()
      .then((res) => {
        if (cancelled) return
        const newVal = (res.po_no || '').trim()
        if (!newVal) return
        setDraft((prev) => (prev.po_no.trim() ? prev : { ...prev, po_no: newVal }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, mode, factoryCode, triggerSuggest])

  // Dismiss alert khi user sửa po_no khác mã vừa suggest.
  useEffect(() => {
    if (regenNotice && draft.po_no !== regenNotice.to) setRegenNotice(null)
  }, [draft.po_no, regenNotice])

  // Master-data combos đọc thẳng bảng master (Access RowSource), thay cho
  // distinctValues derive từ data trên trang.
  const { data: combos } = useGetReferenceCombosQuery({ factoryCode }, { skip: !open })
  // quality_code: Access PO List ORDER BY code → sort lại theo code client-side
  // (server trả theo created_date desc).
  const qualityCodeOptions = useMemo(
    () =>
      injectCurrent(
        [...(combos?.qualityCodes ?? [])]
          .map((o) => o.code)
          .sort((a, b) => a.localeCompare(b, 'vi')),
        draft.quality_code,
      ),
    [combos, draft.quality_code],
  )
  // Label map "<CODE> — <NAME>" (skill convert-form). quality_code → description,
  // bom_code → bom_type. processing_type không có name (RowSource chỉ po_type) →
  // giữ code thuần.
  const qualityCodeNames = useMemo(
    () =>
      Object.fromEntries(
        (combos?.qualityCodes ?? []).map((o) => [o.code, o.description ?? '']),
      ) as Record<string, string>,
    [combos],
  )
  const processingTypeOptions = useMemo(
    () => injectCurrent(combos?.processingTypes?.map((o) => o.po_type) ?? [], draft.processing_type),
    [combos, draft.processing_type],
  )
  const bomCodeOptions = useMemo(
    () => injectCurrent(combos?.bomCodes?.map((o) => o.bom_code) ?? [], draft.bom_code),
    [combos, draft.bom_code],
  )
  const bomCodeNames = useMemo(
    () =>
      Object.fromEntries(
        (combos?.bomCodes ?? []).map((o) => [o.bom_code, o.bom_type ?? '']),
      ) as Record<string, string>,
    [combos],
  )

  // BOM Crop combo — options = crop có recipe cho bom_code; `suggested` = crop
  // chứa po_date (d_date). Re-fetch khi bom_code / po_date đổi.
  const bomCodeTrimmed = draft.bom_code.trim()
  const { data: cropOpt } = useGetBomCropOptionsQuery(
    { factoryCode, bomCode: bomCodeTrimmed, date: draft.po_date || undefined },
    { skip: !open || !bomCodeTrimmed },
  )
  const cropYearOptions = cropOpt?.cropYears ?? []
  const suggestedCrop = cropOpt?.suggested ?? ''

  // BOM info (read-only) — thông số recipe của (bom_code, bom_crop) đã chọn,
  // để user xác nhận đúng BOM trước khi Save. Dùng CHUNG nguồn với page
  // /factory/<code>/data/bom: getFactoryBomList (view có sẵn quality_input/
  // quality_output đã format %) lọc theo crop_year, lấy row khớp bom_code.
  const bomCropTrimmed = draft.bom_crop.trim()
  const { data: bomList, isFetching: bomDetailFetching } = useGetFactoryBomListQuery(
    { factoryCode, cropYear: bomCropTrimmed },
    { skip: !open || !bomCodeTrimmed || !bomCropTrimmed },
  )
  const bomDetail = useMemo(
    () => bomList?.items.find((r) => String(r.bom_code ?? '') === bomCodeTrimmed) ?? null,
    [bomList, bomCodeTrimmed],
  )
  // BOM info luôn hiện ở row 4 (field trống khi chưa chọn BOM); các field dưới
  // cố định row 5-8.
  const rowQuality = 4 // quality_code full-width
  const rowProcPlan = 5 // processing_type | planned_quantity
  const rowInOut = 6 // in_report | out_report
  const rowNote = 7 // note full-width

  // Auto-fill bom_crop = suggestedCrop khi po_date/bom_code đổi — CHỈ khi field
  // trống hoặc còn = suggest lần trước (giữ lựa chọn tay của user). Edit mode:
  // crop đã lưu (≠ '' và ≠ lastAutoCrop) nên không bị đè.
  useEffect(() => {
    if (!open || !suggestedCrop) return
    setDraft((prev) => {
      if (prev.bom_crop !== '' && prev.bom_crop !== lastAutoCrop.current) return prev
      lastAutoCrop.current = suggestedCrop
      return prev.bom_crop === suggestedCrop ? prev : { ...prev, bom_crop: suggestedCrop }
    })
  }, [open, suggestedCrop])

  const poNoEmpty = draft.po_no.trim().length === 0
  const poDateEmpty = draft.po_date.trim().length === 0
  // BOM Code required — enhancement ngoài Access spec (`strCheck` gốc chỉ có
  // po_no,po_date), theo yêu cầu chị Đẹp 2026-05-27.
  const bomCodeEmpty = draft.bom_code.trim().length === 0
  const canSave = canEdit && !poNoEmpty && !poDateEmpty && !bomCodeEmpty && !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Map Access `po_no_AfterUpdate` (DCount duplicate). New mode: onBlur →
  // BE check; nếu trùng → suggest mã mới + alert (KHÔNG auto-save). Edit không
  // check (PK không đổi). Race blur↔Save: precheckBusyRef chặn handleSave.
  const handlePoNoBlur = async () => {
    if (mode === 'edit') return
    const val = draft.po_no.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck({ factoryCode, poNo: val }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network (skill anti #7).
      const next = await triggerSuggest({ factoryCode }, false).unwrap()
      const newVal = (next.po_no || '').trim()
      if (newVal && newVal !== val) {
        setDraft((prev) => ({ ...prev, po_no: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE raise lúc Save nếu thật sự trùng — không poison UX ở blur.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  // Auto-format value sang Standard `#,##0.00` khi user blur khỏi field số.
  const handleNumericBlur = <K extends keyof DraftState>(key: K) => () => {
    const raw = draft[key]
    const formatted = formatStandard(raw)
    if (formatted !== raw) setDraft((prev) => ({ ...prev, [key]: formatted }))
  }

  // Map btnSave_Click (line 2207): isValid(Me,strCheck) → Update_Insert(...).
  //   Parent (FactoryPage) invalidate cache khi onSave resolve === Form.Requery.
  //   Edit mode: parent inject po_no qua getPkPayload (PK authoritative).
  const handleSave = () => {
    if (precheckBusyRef.current) return // race guard: blur check đang chạy
    if (!canSave) return
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const payload: FactoryBRowPayload = {
      po_no: draft.po_no.trim(),
      po_date: str(draft.po_date),
      po_clossing_date: str(draft.po_clossing_date),
      quality_code: str(draft.quality_code),
      // parseNumeric strip commas — payload BE nhận raw number.
      planned_quantity: parseNumeric(draft.planned_quantity),
      in_report_quantity: parseNumeric(draft.in_report_quantity),
      out_report_quantity: parseNumeric(draft.out_report_quantity),
      // Cột `po.is_finished` là BOOLEAN — gửi true/false, KHÔNG gửi 0/1
      // (int → psycopg DatatypeMismatch "boolean but expression is smallint" → 503).
      is_finished: draft.is_finished === '1',
      processing_type: str(draft.processing_type),
      bom_code: str(draft.bom_code),
      bom_crop: str(draft.bom_crop),
      note: str(draft.note),
    }
    onSave(payload)
  }

  // Map btnCancel_Click (line 2201): Me.Undo + DoCmd.Close. Block close khi saving.
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return // accident-proof; user phải bấm Cancel
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : 'Edit'} — Factory · PO List`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : 'Edit'} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' PO List'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {/* btnSave (Access line 600, FontWeight=700) — primary action. */}
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          {/* btnCancel (Access line 655). Block khi saving. */}
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Read-only — you don't have edit permission for this factory.
          </Alert>
        ) : null}
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            PO No <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}

        {/* 2-col grid — layout theo yêu cầu chị Đẹp 2026-06-18 (dồn ô trống
            row1col2, gom field liên quan), KHÔNG còn 1:1 Access Top coord:
            Row 1: po_no              | po_date
            Row 2: bom_code           | bom_crop
            Row 3: BOM info read-only (spans cả 2 cột — chỉ hiện khi đủ key)
            Row 4: quality_code       | processing_type
            Row 5: planned_quantity   | note (spans 5-7)
            Row 6: in_report_quantity |
            Row 7: out_report_quantity|
            (is_finished + po_clossing_date đã tách sang nút "Finished".) */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 — po_no (auto-suggest khi new; PK disabled khi edit).
             onBlur check trùng → regen mã mới (skill access-suggest). */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 }, ...(mode === 'edit' ? readonlyFieldSx : {}) }}
            label="PO No"
            size="small"
            fullWidth
            required
            disabled={mode === 'edit'}
            autoFocus={mode === 'new'}
            value={draft.po_no}
            onChange={(e) => handleChange('po_no', e.target.value)}
            onBlur={() => void handlePoNoBlur()}
            inputProps={{ tabIndex: 1 }}
            InputProps={{
              endAdornment:
                precheckBusy || suggestState.isFetching ? <CircularProgress size={14} /> : null,
            }}
            helperText={
              mode === 'edit'
                ? 'Cannot be changed after creation.'
                : precheckBusy
                  ? 'Verifying PO No…'
                  : suggestState.isFetching
                    ? 'Suggesting next code…'
                    : 'Suggested code below; edit if needed.'
            }
          />

          {/* Row 1 col 2 — po_date (đưa lên cạnh po_no; is_finished/po_clossing_date
              đã tách sang nút "Finished" + PoFinishedDialog trên toolbar PO List). */}
          <DatePicker
            label="PO Date"
            format="DD-MMM-YYYY"
            value={draft.po_date ? dayjs(draft.po_date) : null}
            onChange={(d) =>
              handleChange('po_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                error: poDateEmpty,
                InputLabelProps: { shrink: true },
                inputProps: { tabIndex: 2 },
                sx: { gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } },
              },
            }}
          />

          {/* PO Closing Date đã chuyển sang event nút "Finished" (set hôm nay khi
              → Finished, clear khi → Processing) — KHÔNG còn nhập tay trong form. */}
          <Autocomplete<string, false, false, true>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: rowProcPlan } }}
            freeSolo
            size="small"
            options={processingTypeOptions}
            value={draft.processing_type}
            onChange={(_, v) => handleChange('processing_type', String(v ?? ''))}
            onInputChange={(_, v) => handleChange('processing_type', v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Processing Type"
                fullWidth
                inputProps={{ ...params.inputProps, tabIndex: 6 }}
              />
            )}
          />

          {/* Row 4 — BOM info (read-only) spanning cả 2 cột: thông số recipe
              của (bom_code, bom_crop). Luôn hiện; field trống khi chưa chọn. */}
          <Box
              sx={{
                gridColumn: { xs: 1, sm: '1 / -1' },
                gridRow: { sm: 3 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                gap: 1,
                p: 1,
                borderRadius: 1,
                border: (theme) => `1px dashed ${theme.palette.divider}`,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafbfc',
              }}
            >
              <TextField
                label="BOM Type"
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: bomDetailFetching ? <CircularProgress size={12} /> : null,
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: -1 }}
                value={String(bomDetail?.bom_type ?? '')}
                sx={readonlyFieldSx}
              />
              <TextField
                label="Processing Loss"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                value={formatPercent(bomDetail?.processing_loss)}
                inputProps={{ style: { textAlign: 'right' }, tabIndex: -1 }}
                sx={readonlyFieldSx}
              />
              <TextField
                label="Moisture Loss"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                value={formatPercent(bomDetail?.moisture_loss)}
                inputProps={{ style: { textAlign: 'right' }, tabIndex: -1 }}
                sx={readonlyFieldSx}
              />
              <TextField
                label="Quality Input"
                size="small"
                fullWidth
                multiline
                maxRows={2}
                sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' }, ...readonlyFieldSx }}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: -1 }}
                value={String(bomDetail?.quality_input ?? '')}
              />
              <TextField
                label="Quality Output"
                size="small"
                fullWidth
                multiline
                maxRows={2}
                sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' }, ...readonlyFieldSx }}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: -1 }}
                value={String(bomDetail?.quality_output ?? '')}
              />
              <TextField
                label="BOM Note"
                size="small"
                fullWidth
                multiline
                maxRows={2}
                sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' }, ...readonlyFieldSx }}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: -1 }}
                value={String(bomDetail?.note ?? '')}
              />
          </Box>

          {/* Quality code — full hàng (row 4), dưới BOM info. */}
          <Autocomplete<string, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: '1 / -1' }, gridRow: { sm: rowQuality } }}
            size="small"
            options={qualityCodeOptions}
            getOptionLabel={(o) => comboLabel(o, qualityCodeNames)}
            value={draft.quality_code || null}
            onChange={(_, v) => handleChange('quality_code', String(v ?? ''))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Quality Code"
                fullWidth
                inputProps={{ ...params.inputProps, tabIndex: 5 }}
              />
            )}
          />
          <Autocomplete<string, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 } }}
            size="small"
            options={bomCodeOptions}
            getOptionLabel={(o) => comboLabel(o, bomCodeNames)}
            value={draft.bom_code || null}
            onChange={(_, v) => handleChange('bom_code', String(v ?? ''))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="BOM Code"
                required
                fullWidth
                error={bomCodeEmpty}
                inputProps={{ ...params.inputProps, tabIndex: 3 }}
              />
            )}
          />

          {/* Row 3 col 2 — BOM Crop (crop-year recipe cho bom_code; suggest theo
              PO Date). Field mới ngoài Access spec — đặt cạnh BOM Code. */}
          <Autocomplete<string, false, false, true>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 } }}
            freeSolo
            size="small"
            options={cropYearOptions}
            value={draft.bom_crop}
            onChange={(_, v) => {
              const val = String(v ?? '')
              handleChange('bom_crop', val)
              lastAutoCrop.current = '' // user chọn tay → khoá auto-suggest đè tiếp
            }}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') {
                handleChange('bom_crop', v)
                lastAutoCrop.current = ''
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="BOM Crop"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, tabIndex: 4 }}
                helperText={
                  suggestedCrop && draft.bom_crop === suggestedCrop
                    ? `Suggested from PO Date (${suggestedCrop}).`
                    : 'Crop-year of the BOM recipe used.'
                }
              />
            )}
          />

          {/* Row 5 col 2 — planned_quantity (cạnh processing_type). */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: rowProcPlan } }}
            label="Planned Quantity"
            size="small"
            fullWidth
            type="text"
            value={draft.planned_quantity}
            onChange={(e) => handleChange('planned_quantity', e.target.value)}
            onBlur={handleNumericBlur('planned_quantity')}
            inputProps={{ ...numericInputProps, tabIndex: 7 }}
          />
          {/* Row 6 — in_report_quantity | out_report_quantity. */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: rowInOut } }}
            label="In Report Quantity"
            size="small"
            fullWidth
            type="text"
            value={draft.in_report_quantity}
            onChange={(e) => handleChange('in_report_quantity', e.target.value)}
            onBlur={handleNumericBlur('in_report_quantity')}
            inputProps={{ ...numericInputProps, tabIndex: 8 }}
          />

          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: rowInOut } }}
            label="Out Report Quantity"
            size="small"
            fullWidth
            type="text"
            value={draft.out_report_quantity}
            onChange={(e) => handleChange('out_report_quantity', e.target.value)}
            onBlur={handleNumericBlur('out_report_quantity')}
            inputProps={{ ...numericInputProps, tabIndex: 9 }}
          />

          {/* Row 7 — note full hàng (cuối form). */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: '1 / -1' }, gridRow: { sm: rowNote } }}
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
            inputProps={{ tabIndex: 10 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PoListFormDialog
